/**
 * Contract Card Component
 * Displays a linked contract in a message thread - WhatsApp style card
 */

import React, { useState, useEffect } from 'react';
import { FileText, Calendar, User, CheckCircle, Clock, XCircle, ExternalLink, AlertCircle } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { AgreementDocument } from '@/firebase/agreementService';

interface ContractCardProps {
  contractId: string;
  organizationId: string;
  onViewContract?: (contract: AgreementDocument) => void;
}

const ContractCard: React.FC<ContractCardProps> = ({ contractId, organizationId, onViewContract }) => {
  const [contract, setContract] = useState<AgreementDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadContract();
  }, [contractId, organizationId]);

  const loadContract = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch contract document
      const contractRef = doc(db, 'agreementDocuments', contractId);
      const contractSnap = await getDoc(contractRef);

      if (!contractSnap.exists()) {
        setError('Contract not found');
        setLoading(false);
        return;
      }

      const contractData = { id: contractSnap.id, ...contractSnap.data() } as AgreementDocument;
      setContract(contractData);
    } catch (err: any) {
      console.error('Error loading contract:', err);
      setError(err.message || 'Failed to load contract');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'signed': return 'bg-green-100 text-green-800 border-green-200';
      case 'sent': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'draft': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'expired': return 'bg-red-100 text-red-800 border-red-200';
      case 'cancelled': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'signed': return <CheckCircle className="w-4 h-4" />;
      case 'sent': return <Clock className="w-4 h-4" />;
      case 'expired': return <AlertCircle className="w-4 h-4" />;
      case 'cancelled': return <XCircle className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const formatDate = (date: any): string | null => {
    if (!date) return null;
    
    let dateObj: Date;
    if (date?.toDate) {
      dateObj = date.toDate();
    } else if (date?._seconds) {
      dateObj = new Date(date._seconds * 1000);
    } else if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'number') {
      dateObj = new Date(date);
    } else {
      return null;
    }

    const now = new Date();
    const diffMs = dateObj.getTime() - now.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';
    if (diffDays > 0 && diffDays < 7) return `In ${diffDays} days`;
    if (diffDays < 0 && diffDays > -7) return `${Math.abs(diffDays)} days ago`;

    return dateObj.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: dateObj.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
    });
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-3 my-2 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
      </div>
    );
  }

  if (error || !contract) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-3 my-2">
        <p className="text-sm text-red-800">{error || 'Contract not found'}</p>
      </div>
    );
  }

  const signersCount = contract.signers?.length || 0;
  const signedCount = contract.signers?.filter(s => s.status === 'signed').length || 0;
  const expiresAt = contract.expiresAt ? formatDate(contract.expiresAt) : null;

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg p-3 my-2 hover:bg-gray-50 transition-colors cursor-pointer"
      onClick={() => onViewContract && onViewContract(contract)}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center space-x-2 flex-1 min-w-0">
          <FileText className="w-4 h-4 text-purple-600 flex-shrink-0" />
          <h4 className="text-sm font-semibold text-gray-900 truncate">
            {contract.name}
          </h4>
        </div>
        {onViewContract && (
          <ExternalLink className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 ml-2" />
        )}
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center space-x-2">
          <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded text-xs font-medium border ${getStatusColor(contract.status)}`}>
            {getStatusIcon(contract.status)}
            <span className="capitalize">{contract.status}</span>
          </span>
        </div>

        {contract.description && (
          <div className="text-xs text-gray-600 line-clamp-2">
            {contract.description}
          </div>
        )}

        {signersCount > 0 && (
          <div className="flex items-center space-x-2 text-xs text-gray-600">
            <User className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{signedCount} of {signersCount} signed</span>
          </div>
        )}

        {expiresAt && (
          <div className="flex items-center space-x-2 text-xs text-gray-600">
            <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
            <span>Expires: {expiresAt}</span>
          </div>
        )}

        {contract.locationName && (
          <div className="text-xs text-gray-500">
            Location: {contract.locationName}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractCard;

